/**
 * ============================================================================
 * RecordCalibrationDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: capture a new calibration record. Includes:
 *            - performed-at timestamp
 *            - source picker (INTERNAL / EXTERNAL_VENDOR)
 *            - outcome picker (PASSED / FAILED / OUT_OF_TOLERANCE)
 *            - technician signature
 *            - supervisor co-sign (required for INTERNAL source)
 *            - vendor supplier id (required for EXTERNAL_VENDOR)
 *            - free-form measurements JSON
 *
 * A11Y: the JSON editor is a <textarea> with monospace font and an inline
 *  parse-error hint; focus moves to the first field; Escape closes.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './CalibrationTray.module.css';
import {
  useRecordCalibrationMutation,
  type RecordCalibrationInput,
} from './useRecordCalibrationMutation';
import type { CalibrationTask } from './useCalibrationTrayQuery';
import type {
  CalibrationOutcome,
  CalibrationSource,
} from '../EquipmentDetail/useEquipmentDetailQuery';

const OUTCOMES: CalibrationOutcome[] = ['PASSED', 'FAILED', 'OUT_OF_TOLERANCE'];
const SOURCES: CalibrationSource[] = ['INTERNAL', 'EXTERNAL_VENDOR'];

export interface RecordCalibrationDialogProps {
  task: CalibrationTask;
  onClose: () => void;
  onRecorded: () => void;
}

export function RecordCalibrationDialog({
  task,
  onClose,
  onRecorded,
}: RecordCalibrationDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLInputElement | null>(null);

  const recordM = useRecordCalibrationMutation();

  const nowIso = new Date().toISOString().slice(0, 16); // datetime-local
  const [performedAt, setPerformedAt] = useState<string>(nowIso);
  const [source, setSource] = useState<CalibrationSource>('INTERNAL');
  const [outcome, setOutcome] = useState<CalibrationOutcome>('PASSED');
  const [technicianSig, setTechnicianSig] = useState('');
  const [supervisorSig, setSupervisorSig] = useState('');
  const [vendorSupplierId, setVendorSupplierId] = useState('');
  const [measurementsText, setMeasurementsText] = useState<string>(
    '{\n  "temperature_c": 23.5,\n  "humidity_pct": 41\n}',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);

    let measurements: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(measurementsText);
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        setError(
          t('equipment.calibrationTray.dialog.errors.measurementsObject', {
            defaultValue: 'Measurements must be a JSON object.',
          }),
        );
        return;
      }
      measurements = parsed as Record<string, unknown>;
    } catch {
      setError(
        t('equipment.calibrationTray.dialog.errors.measurementsJson', {
          defaultValue: 'Measurements must be valid JSON.',
        }),
      );
      return;
    }

    if (!technicianSig.trim()) {
      setError(
        t('equipment.calibrationTray.dialog.errors.technicianSig', {
          defaultValue: 'Technician signature bundle id is required.',
        }),
      );
      return;
    }
    if (source === 'INTERNAL' && !supervisorSig.trim()) {
      setError(
        t('equipment.calibrationTray.dialog.errors.supervisorSig', {
          defaultValue:
            'Internal calibrations require a supervisor co-signature.',
        }),
      );
      return;
    }
    if (
      source === 'INTERNAL' &&
      supervisorSig.trim() === technicianSig.trim()
    ) {
      setError(
        t('equipment.calibrationTray.dialog.errors.distinctSigs', {
          defaultValue:
            'Technician and supervisor signature bundles must be distinct.',
        }),
      );
      return;
    }
    if (source === 'EXTERNAL_VENDOR' && !vendorSupplierId.trim()) {
      setError(
        t('equipment.calibrationTray.dialog.errors.vendor', {
          defaultValue: 'External calibrations require a vendor supplier id.',
        }),
      );
      return;
    }

    const input: RecordCalibrationInput = {
      equipmentId: task.equipment_id,
      performed_at_utc: new Date(performedAt).toISOString(),
      source,
      outcome,
      technician_signature_bundle_id: technicianSig.trim(),
      supervisor_signature_bundle_id:
        source === 'INTERNAL' ? supervisorSig.trim() : undefined,
      vendor_supplier_id:
        source === 'EXTERNAL_VENDOR' ? vendorSupplierId.trim() : undefined,
      measurements,
    };

    try {
      await recordM.mutateAsync(input);
      onRecorded();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('equipment.calibrationTray.dialog.errors.serverFailed', {
              defaultValue: 'Server rejected the calibration record.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.calibrationTray.dialog.title', {
              defaultValue: 'Record calibration',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.calibrationTray.dialog.subtitle', {
              defaultValue:
                'Record a calibration for {{tag}} ({{name}}). Internal calibrations require a supervisor co-signature.',
              tag: task.equipment_tag,
              name: task.equipment_name,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="cal-performed" className={dialogStyles.label}>
                {t('equipment.calibrationTray.dialog.performedAt', {
                  defaultValue: 'Performed at',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="cal-performed"
                ref={firstRef}
                type="datetime-local"
                className={dialogStyles.input}
                value={performedAt}
                onChange={(e) => setPerformedAt(e.target.value)}
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="cal-source" className={dialogStyles.label}>
                {t('equipment.calibrationTray.dialog.source', {
                  defaultValue: 'Source',
                })}
              </label>
              <select
                id="cal-source"
                className={dialogStyles.select}
                value={source}
                onChange={(e) => setSource(e.target.value as CalibrationSource)}
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {t(`equipment.detail.calibrations.source.${s}`, {
                      defaultValue: s,
                    })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="cal-outcome" className={dialogStyles.label}>
              {t('equipment.calibrationTray.dialog.outcome', {
                defaultValue: 'Outcome',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <select
              id="cal-outcome"
              className={dialogStyles.select}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as CalibrationOutcome)}
            >
              {OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {t(`equipment.detail.calibrations.outcome.${o}`, {
                    defaultValue: o,
                  })}
                </option>
              ))}
            </select>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="cal-tech" className={dialogStyles.label}>
              {t('equipment.calibrationTray.dialog.technicianSig', {
                defaultValue: 'Technician signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="cal-tech"
              className={dialogStyles.input}
              value={technicianSig}
              onChange={(e) => setTechnicianSig(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          {source === 'INTERNAL' && (
            <div className={dialogStyles.field}>
              <label htmlFor="cal-sup" className={dialogStyles.label}>
                {t('equipment.calibrationTray.dialog.supervisorSig', {
                  defaultValue: 'Supervisor signature bundle id',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="cal-sup"
                className={dialogStyles.input}
                value={supervisorSig}
                onChange={(e) => setSupervisorSig(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          )}

          {source === 'EXTERNAL_VENDOR' && (
            <div className={dialogStyles.field}>
              <label htmlFor="cal-vendor" className={dialogStyles.label}>
                {t('equipment.calibrationTray.dialog.vendor', {
                  defaultValue: 'Vendor supplier id',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="cal-vendor"
                className={dialogStyles.input}
                value={vendorSupplierId}
                onChange={(e) => setVendorSupplierId(e.target.value)}
                placeholder="sup-…"
                required
              />
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="cal-meas" className={dialogStyles.label}>
              {t('equipment.calibrationTray.dialog.measurements', {
                defaultValue: 'Measurements (JSON object)',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="cal-meas"
              className={styles.measurementsArea}
              value={measurementsText}
              onChange={(e) => setMeasurementsText(e.target.value)}
              aria-describedby="cal-meas-hint"
              required
            />
            <p id="cal-meas-hint" className={styles.measurementsHint}>
              {t('equipment.calibrationTray.dialog.measurementsHint', {
                defaultValue:
                  'JSON object — keys are measurement names, values are observed readings.',
              })}
            </p>
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={recordM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={recordM.isPending}>
              {t('equipment.calibrationTray.dialog.submit', {
                defaultValue: 'Record',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
